import mixpanel from "mixpanel-browser";
import { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { styled, useTheme } from "styled-components";
import { routeList } from "../../App";
import { lang } from "../../assets/languages/en.language";
import { AgeRangeInput } from "../../components/form/AboutYouConnectWith/AgeRange.input";
import { BirthDateInput } from "../../components/form/AboutYouConnectWith/BirthDate.input";
import { MasterTypeInput } from "../../components/form/AboutYouConnectWith/MasterType.input";
import { MasterTypesInput } from "../../components/form/AboutYouConnectWith/MasterTypes.input";
import AppLogo from "../../components/svgs/AppLogo";
import api from "../../services/api";
import { useUserStore } from "../../store/useUserStore";
import { PurpleButton } from "../../styles/Button.style";
import { MainContainer, RootContainer } from "../../styles/Container.style";
import { ErrorMessage, StyledForm } from "../../styles/Form";
import { Paragraph, Title } from "../../styles/Text";
import { Column, Gap } from "../../styles/Utils";
import { AppLogoWrapper } from "../VerifyAccount.page";

// Define the types for the form data
export interface IFormInput {
  birthDate: string;
  youRole: string;
  themRole: string[];
  ageFrom: number | string;
  ageTo: number | string;
}

export default function AboutYouConnectWithPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const {
    setAboutYouConnectWith,
    profile: { birthDate, youRole, themRole, ageFrom, ageTo },
  } = useUserStore();

  useEffect(() => {
    if ([birthDate, youRole, themRole, ageFrom, ageTo].every((e) => e != null)) navigate(routeList.verifyAccount);
  });

  const methods = useForm<IFormInput>({
    mode: "onChange",
    defaultValues: {
      themRole: [],
      ageFrom: 18,
      ageTo: 100,
    },
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = methods;

  const onSubmit = async (data: IFormInput) => {
    const { ageFrom, ageTo } = data;
    const payload = {
      ...data,
      ageFrom: ageFrom as number,
      ageTo: ageTo as number,
    };
    try {
      await api.updateAboutYouConnectWith(payload);
      setAboutYouConnectWith(payload);
      mixpanel.track("Profile Completed");
      navigate(routeList.addPictures);
    } catch (err) {
      console.log("Error updating <you connect with> infos", err);
      alert("Error updating <you connect with> infos");
    }
  };

  return (
    <RootContainer $topSafe={true} $background={theme.color.primaryExtraLight}>
      <Column $alignItem="center" $maxHeight>
        <AppLogoWrapper>
          <AppLogo color={theme.color.tertiaryLight} />
        </AppLogoWrapper>
        <MainContainer $padding="3rem 1rem">
          <FormProvider {...methods}>
            <StyledForm>
              <Title>{lang.addYouConnectWithPage.title}</Title>
              <Gap $height={1} />
              <Paragraph>{lang.addYouConnectWithPage.message}</Paragraph>
              <Gap $height={1} />
              <BirthDateInput register={register} name="birthDate" />
              {errors.birthDate && <ErrorMessage>{errors.birthDate.message}</ErrorMessage>}
              <Gap $height={1} />
              <MasterTypeInput who="You are" register={register} name="youRole" />
              <Gap $height={1} />
              <Title>{lang.addYouConnectWithPage.title2}</Title>
              <Gap $height={1} />
              <Paragraph>{lang.addYouConnectWithPage.message2}</Paragraph>
              <Gap $height={1} />
              <Controller
                name="ageFrom"
                control={control}
                rules={{ required: "L'âge de départ est requis" }}
                render={({ field }) => (
                  <Controller
                    name="ageTo"
                    control={control}
                    rules={{ required: "L'âge de fin est requis" }}
                    render={({ field: field2 }) => (
                      <AgeRangeInput
                        value1={field.value}
                        value2={field2.value}
                        onChange1={field.onChange}
                        onChange2={field2.onChange}
                      />
                    )}
                  />
                )}
              />
              <Gap $height={1} />
              <Controller
                name="themRole"
                control={control}
                rules={{ required: "This field is required" }} // Enforce required validation
                render={({ field }) => <MasterTypesInput who="Them" value={field.value} onChange={field.onChange} />}
              />
              <Gap $height={1} />
            </StyledForm>
          </FormProvider>
        </MainContainer>
        <Gap $height={1.5} />
        <PurpleButton type="submit" disabled={!isValid} onClick={handleSubmit(onSubmit)}>
          <span>{lang.buttons.continue}</span>
        </PurpleButton>
      </Column>
    </RootContainer>
  );
}

export const SelectInput = styled.select<{ $marginInline?: string }>`
  background: inherit;
  color: inherit;
  border: none;
  outline: none;
  cursor: pointer;
  font-size: inherit;
  -webkit-appearance: none;
  -moz-appearance: none;
  margin-inline: ${(props) => props.$marginInline};
  text-align: end;
  text-align-last: end; // Safari
`;

export const InputWrapper = styled.div`
  padding: 1rem 2rem;
  width: 100%;
  background: ${(props) => props.theme.color.primary};
  color: ${(props) => props.theme.color.tertiaryLight};
  font-size: ${(props) => props.theme.font.size.title.medium};
  font-family: ${(props) => props.theme.font.family.title};
  border-radius: 30px;
`;
